/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Header } from '@mairistem/composants';
import { PanelButtonEdition } from '@mairistem/administres-utils';

import { Divider } from '@/components/Common';

import BlocChamps from './BlocChamps';
import type { SectionChamps } from './BlocChamps';
import { ONGLETS_PARENT } from './TabsPersonnes';
import type { PersonneTab } from './TabsPersonnes';

interface InformationsIndividuProps {
  individu?: any;
  /**
   * Tous les individus du dossier, indexés par clé.
   *
   * Consulté uniquement quand une section déclare un `cleIndividu` — le cas des
   * onglets de parents d'un mariage, qui en montrent deux. Ailleurs, `individu`
   * suffit.
   */
  individus?: Record<string, any>;
  /** Onglet actif : détermine la modale ouverte et le numéro du parent. */
  tab: PersonneTab;
  /**
   * Sections à afficher, produites par `FICHE_CONFIG[type].blocs.individu`.
   * La première porte le titre affiché à côté du crayon ; les suivantes sont
   * précédées d'un séparateur.
   */
  sections: SectionChamps[];
  /** Identifiant de l'acte : racine du payload et URL du PUT. */
  identifiant?: number;
  /**
   * Date de naissance de l'INDIVIDU PRINCIPAL (l'enfant d'une naissance, le
   * défunt d'un décès), pour le contrôle « parent né avant lui ».
   */
  naissanceDateIndividuPrincipal?: string;
  /** Endpoint générique des individus, transmis aux modales (cf. leur JSDoc). */
  urlIndividus?: string;
  /**
   * Modale d'édition de l'individu de CET onglet, hors parents — déjà résolue
   * par l'appelant depuis `FICHE_CONFIG.modales.individus[onglet]`. Son absence
   * rend le bloc en lecture seule.
   */
  ModalIndividu?: React.ComponentType<any>;
  /** Modale d'édition d'un parent ; idem. */
  ModalParent?: React.ComponentType<any>;
  /** Props propres au type d'acte, transmises à la modale du parent. */
  propsModalParent?: Record<string, unknown>;
  /**
   * L'acte du dossier, transmis à la modale de l'individu.
   *
   * Nécessaire pour le défunt d'un décès : sa section « ETAT MATRIMONIAL »
   * affiche des champs portés par `ec_actd`, que sa modale doit pouvoir écrire.
   */
  acte?: any;
  /** Rafraîchit l'acte quand la modale d'un individu l'a modifié. */
  onActeSaved?: (acte: any) => void;
  service?: { update: (data: Record<string, any>) => Promise<any> };
  /**
   * Individu enregistré, et la clé du dossier où le ranger.
   *
   * ⚠️ La clé est indispensable dès qu'un onglet porte plusieurs individus : sans
   * elle, l'appelant rangerait le parent 2 d'un mariage sous le nom de l'onglet,
   * et le panneau afficherait encore l'ancienne valeur jusqu'au rechargement.
   * Absente, l'appelant retombe sur l'individu de l'onglet.
   */
  onSaved: (individu: any, cleIndividu?: string) => void;
}

/** Un groupe de sections partageant un crayon et une modale. */
interface GroupeSections {
  Modal?: React.ComponentType<any>;
  sections: SectionChamps[];
  /** Clé du dossier décrite par ce groupe, si sa première section la déclare. */
  cleIndividu?: string;
  /** Rang du parent édité par ce groupe, si sa première section le déclare. */
  numeroParent?: 1 | 2;
  /**
   * La modale de ce groupe vient-elle de sa SECTION, et non du défaut de
   * l'onglet ?
   *
   * ⚠️ C'est ce qui décide du contrat de props. Sur un onglet de parents, les
   * groupes des parents reçoivent la modale de l'onglet (`ModalParent`, qui
   * attend `parent` et `numeroParent`) ; un groupe qui déclare la sienne édite
   * autre chose — l'état matrimonial du COUPLE, porté par l'acte — et attend
   * donc `acte` et `onActeSaved`.
   */
  modaleDeSection?: boolean;
}

/**
 * Découpe les sections en groupes : une section qui déclare une `modale` en
 * ouvre un nouveau, les autres rejoignent le précédent. Le premier groupe est
 * édité par la modale de l'onglet.
 *
 * Découper évite la modale géante — quatre sections d'un seul bloc donnaient une
 * modale à dérouler. Sans `modale` déclarée nulle part, il n'y a qu'un groupe.
 */
const grouperSections = (
  sections: SectionChamps[],
  ModalOnglet?: React.ComponentType<any>,
): GroupeSections[] => {
  const groupes: GroupeSections[] = [];

  for (const section of sections) {
    /*
     * Une section ouvre un groupe si elle déclare une modale OU un individu :
     * sur un onglet de parents de mariage, les deux parents partagent la même
     * modale (`ModalParent`) et seule la clé d'individu les distingue.
     */
    const ouvreUnGroupe = Boolean(section.modale ?? section.cleIndividu);

    if (groupes.length === 0 || ouvreUnGroupe) {
      groupes.push({
        Modal: section.modale ?? ModalOnglet,
        sections: [section],
        cleIndividu: section.cleIndividu,
        numeroParent: section.numeroParent,
        modaleDeSection: Boolean(section.modale),
      });
    } else {
      groupes[groupes.length - 1].sections.push(section);
    }
  }

  return groupes;
};

const InformationsIndividu = (props: InformationsIndividuProps) => {
  const {
    individu,
    individus,
    tab,
    sections,
    identifiant,
    naissanceDateIndividuPrincipal,
    urlIndividus,
    ModalIndividu,
    ModalParent,
    propsModalParent,
    acte,
    service,
    onSaved,
    onActeSaved,
  } = props;

  /** Index du groupe en cours d'édition, `null` si aucune modale n'est ouverte. */
  const [groupeEnEdition, setGroupeEnEdition] = React.useState<number | null>(
    null,
  );

  // ⚠️ Test explicite sur les onglets de parent, et non `tab !== 'enfant'` :
  // un onglet DÉFUNT ou DÉCLARANT serait sinon traité comme un parent et
  // recevrait `ModalParent`.
  const isParent = ONGLETS_PARENT.includes(tab);
  const numeroParent = tab === 'parent2' ? 2 : 1;

  /**
   * Individu d'un groupe : celui qu'il déclare, sinon celui de l'onglet.
   *
   * C'est ce qui permet à un onglet de porter plusieurs individus — les deux
   * parents d'un conjoint de mariage — sans que les trois autres types d'acte
   * changent de comportement.
   */
  const individuDuGroupe = (groupe: GroupeSections) =>
    groupe.cleIndividu ? individus?.[groupe.cleIndividu] : individu;

  const groupes = React.useMemo(
    () => grouperSections(sections, isParent ? ModalParent : ModalIndividu),
    [sections, isParent, ModalParent, ModalIndividu],
  );

  const fermer = React.useCallback(() => setGroupeEnEdition(null), []);

  return (
    <>
      {groupes.map((groupe, index) => {
        const [sectionPrincipale, ...sectionsSuivantes] = groupe.sections;

        return (
          <React.Fragment key={sectionPrincipale?.titre ?? index}>
            {/*
              Trait entre GROUPES seulement : deux sections qui partagent un
              crayon ne doivent pas paraître éditables séparément.
            */}
            {index > 0 && sectionPrincipale?.separateur && (
              // Plus d'air que le trait intra-section (`1em 1em 0`) : celui-ci
              // sépare deux blocs ÉDITABLES, pas deux sections d'un même bloc.
              <Divider style={{ margin: '1.5em 1em 0' }} />
            )}
            <div className="informations-individu-panel">
              <div
                className="informations-individu-header"
                style={index === 0 ? undefined : { marginTop: '1em' }}
              >
                <Header
                  as="h5"
                  color="blue"
                  style={{
                    marginLeft: '1em',
                    marginTop: 0,
                    /*
                     * `0` seulement pour le premier groupe — le titre y partage
                     * sa ligne avec le crayon et ouvre le panneau, et c'est ce
                     * que figent les snapshots livrés.
                     *
                     * Les groupes suivants laissent la marge basse par défaut de
                     * Semantic, celle qu'ont les titres de section ordinaires
                     * (« ETAT MATRIMONIAL », « DOMICILIATION »). La forcer à `0`
                     * collait « DIVERS » à sa profession, alors que ses voisins
                     * respiraient.
                     */
                    ...(index === 0 && { marginBottom: 0 }),
                  }}
                >
                  {sectionPrincipale?.titre}
                </Header>
                {groupe.Modal && (
                  <PanelButtonEdition
                    onClick={() => setGroupeEnEdition(index)}
                    // Le premier garde son `data-testid` historique : il est
                    // dans les snapshots livrés.
                    data-testid={
                      index === 0
                        ? 'panel-edit-naissance'
                        : `panel-edit-individu-${index}`
                    }
                  />
                )}
              </div>
              <BlocChamps
                lignes={sectionPrincipale?.lignes ?? []}
                styleLigne={{ paddingLeft: '2.5em' }}
              />
            </div>
            {sectionsSuivantes.map((section) => (
              <div key={section.titre} style={{ position: 'relative' }}>
                {section.separateur && (
                  <Divider style={{ margin: '1em 1em 0' }} />
                )}
                <Header
                  as="h5"
                  color="blue"
                  style={{ marginLeft: '1em', marginTop: '1em' }}
                >
                  {section.titre}
                </Header>
                <BlocChamps
                  lignes={section.lignes}
                  styleLigne={{ paddingLeft: '2.5em' }}
                />
              </div>
            ))}
          </React.Fragment>
        );
      })}

      {groupes.map((groupe, index) => {
        const { Modal } = groupe;
        const individuGroupe = individuDuGroupe(groupe);

        if (!Modal) {
          return null;
        }

        /*
         * Une modale déclarée par la SECTION n'est pas une modale de parent :
         * elle édite l'acte, pas l'individu. Cf. `GroupeSections.modaleDeSection`.
         */
        return isParent && !groupe.modaleDeSection ? (
          <Modal
            // biome-ignore lint/suspicious/noArrayIndexKey: un groupe n'a pas d'autre identité
            key={index}
            {...propsModalParent}
            open={groupeEnEdition === index}
            numeroParent={groupe.numeroParent ?? numeroParent}
            identifiant={identifiant}
            acte={acte}
            parent={individuGroupe}
            naissanceDateEnfant={naissanceDateIndividuPrincipal}
            urlIndividus={urlIndividus}
            service={service}
            onClose={fermer}
            onSaved={(individuEnregistre: any) =>
              onSaved(individuEnregistre, groupe.cleIndividu)
            }
          />
        ) : (
          <Modal
            // biome-ignore lint/suspicious/noArrayIndexKey: un groupe n'a pas d'autre identité
            key={index}
            open={groupeEnEdition === index}
            identifiant={identifiant}
            /*
             * Deux noms pour la même valeur, le temps d'un lot : `individu`
             * est le contrat des modales génériques (défunt, déclarant),
             * `enfant` celui de `ModalEnfant`, partagé par la naissance et la
             * reconnaissance.
             */
            individu={individuGroupe}
            enfant={individuGroupe}
            acte={acte}
            urlIndividus={urlIndividus}
            service={service}
            onClose={fermer}
            onSaved={(individuEnregistre: any) =>
              onSaved(individuEnregistre, groupe.cleIndividu)
            }
            onActeSaved={onActeSaved}
          />
        );
      })}
    </>
  );
};

export default InformationsIndividu;
